// @ts-strict-ignore
import { IArticleInsertProcessor } from './i-article-insert.processor';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleVO } from '@article/model/vo/article.vo';
import { Line } from '@3d-scene/model/line';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { Matrix4, Vector3 } from 'three';
import { ConnectArticleHelper } from '@article/helper/connect-article.helper';
import { BaseArticleInsertProcessor } from './base-article-insert.processor';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { CompositionContainer } from '@3d-scene/model3d/composition-container';
import { Injectable } from '@angular/core';
import { Log } from '@shared/debug/log';
import { AttachedArticleContainer } from '../../../service/article-container-attached.service';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { LineComponentUtils } from '@3d-scene/model3d/util/line-component-utils';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ProjectHelper } from '@project/helper/project.helper';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ProjectModel } from '@project/model/project.model';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { UserModel } from '@user/model/user.model';
import { RotateArticlePresenter } from '../../../presenter/rotate-article.presenter';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';

/**
 * Insert processor for adding any article at the origin of the coordinate system without considering any connections.
 */
@Injectable({providedIn: 'root'})
export class GenericArticleInsertProcessor extends BaseArticleInsertProcessor implements IArticleInsertProcessor {

  constructor(
    userModel: UserModel,
    globalContainer: GlobalContainer,
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    projectHelper: ProjectHelper,
    localeResourceManager: LocaleResourceManager,
    rotateArticlePresenter: RotateArticlePresenter,
    containerService: ContainerService,
    lineComponentUtils: LineComponentUtils,
    connectionInfoService: ConnectionInfoService,
    articleContainerLoader: ArticleContainerLoader,
    compositeArticleCacheManager: CompositeArticleCacheManager,
    articleContainerFactory: ArticleContainerFactory,
    articleManager: ArticleManager,
    compositeArticleUtils: CompositeArticleUtils,
    articleContainerUtils: ArticleContainerUtils,
    detachArticleUsageService: DetachArticleUsageService,
  ) {
    super(
      userModel,
      globalContainer,
      projectModel,
      projectModelService,
      projectArticleService,
      projectMetaElementService,
      projectHelper,
      localeResourceManager,
      rotateArticlePresenter,
      containerService,
      lineComponentUtils,
      connectionInfoService,
      articleContainerLoader,
      compositeArticleCacheManager,
      articleContainerFactory,
      articleManager,
      compositeArticleUtils,
      articleContainerUtils,
      detachArticleUsageService,
    );
  }

  public findGivingCubeByConnectionArea(containers: ArticleContainer[],
                                        baseContainer: ArticleContainer,
                                        connectionArea: ConnectionAreaVO): Connection {
    return null;
  }

  public createArticleContainersWrapper(article: ArticleVO): ArticleContainersWrapper {
    const articlesWrapper: ArticleContainersWrapper = new ArticleContainersWrapper(
      article,
      null,
      ArticleTypeConstants.EXCLUDED_TYPES_FROM_COMPOSITE_ARTICLE,
      this.projectModel,
      this.projectArticleService,
      this.articleContainerLoader,
      this.compositeArticleCacheManager,
      this.articleContainerFactory,
      this.articleManager,
      this.articleContainerUtils,
      this.detachArticleUsageService
    );
    articlesWrapper.isArticleOnRail = false;
    return articlesWrapper;
  }

  public process(
    containers: ArticleContainer[],
    baseContainers: ArticleContainer[],
    articleLine: Line,
    acceptingConnectionCube: Connection,
    givingConnectionCubes: Connection[],
    rotationAngle: number,
    excludedUsageIds: string[] = null,
    isMessageDispatchRequired: boolean = true
  ): void {
    const rotationMatrix: Matrix4 = new Matrix4();
    const offset: Vector3 = new Vector3(0, 0, 0);
    ConnectArticleHelper.connectArticleContainers(containers, rotationMatrix, offset);
    if (isMessageDispatchRequired) {
      this.dispatchMessages(containers);
    }
  }

  protected dispatchMessages(givingContainers: ArticleContainer[]): void {
    if (!this.globalContainer.children || this.globalContainer.children.length === 0) {
      Log.error('Composition container required for adding article. Consider creating a new project first.');
      return;
    }
    const compositionContainer: CompositionContainer = this.containerService.findCompositionContainersInGlobal()[0];
    for (const container of givingContainers) {
      const data: AttachedArticleContainer = {
        articleGUID: container.articleGUID,
        compositionId: compositionContainer.compositionGuid,
        container: container,
        connectedTo: ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(container.articleUsage),
      };
      this.containerService.attachArticleContainer(data)
    }
  }
}
